import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { useAppContext } from "../Contexts/AppContext";
import { useRegistrationContext } from "../Contexts/RegistrationContext";
import { config, IInputFieldConfig } from "../Map/config";
import { Input, PageId } from "../Map/enums";
import { IEsriFieldInfo, updateFeatures } from "../Map/rest";
import { Form } from "./Form";

const formId = PageId.EventForm;

export const EventForm = (): JSX.Element => {
    const [isFormValid, setIsFormValid] = useState<number>(null);
    const [formData, setFormData] = useState({});
    const [error, setError] = useState<boolean>(false);

    const { fieldInfos } = useAppContext();

    const {
        formFeature,
        rawData,
        setRawData,
        setOverlay,
        validateForm,
        resetForm,
        onFormUpdated,
        setProceedClicked,
        proceedClicked
    } = useRegistrationContext();

    useEffect(() => {
        const date = new Date(Date.now());
        const today =
            date.getUTCFullYear() +
            "-" +
            (date.getUTCMonth() + 1).toString().padStart(2, "0") +
            "-" +
            date.getUTCDate().toString().padStart(2, "0");

        config.forms.eventForm.fields.map((f) => {
            if (f.field === "DATEFROM" || f.field === "DATETO") {
                f.minDate = today;
            }
        });
    }, [formFeature]);

    useEffect(() => {
        if (isFormValid && !formFeature) {
            console.log("Can not update feature without OBJECTID");
            setError(true);
            return;
        }
        isFormValid && proceedClicked && onFormValid();
    }, [isFormValid, proceedClicked]);

    const onFormValid = async () => {
        setOverlay({ isLoading: true });
        const updateResults = await updateFeatures([appendFormData()], "3");
        if (updateResults.status === 403 && updateResults.statusText === "Forbidden") {
            console.log("ReCaptcha authorization failed");
            setOverlay({ warning: config.reCaptchaErrorMessage });
            return;
        }
        if (!updateResults[0].success) {
            return;
        }
        setOverlay(null);
        onFormUpdated();
    };

    const appendFormData = useCallback(() => {
        const newprops = {};
        config.forms.eventForm.fields.map((field) => {
            newprops[field.field] =
                field.type === Input.Checkbox
                    ? convertBooleanToCodedValue(
                          fieldInfos.find((info) => info.name === field.field),
                          field,
                          formData
                      )
                    : formData[field.field];
        });
        formFeature.setProperties(newprops);
        return formFeature;
    }, [formData]);

    const onFormValidated = (isValid: boolean) => {
        isValid ? setFormData(rawData) : setProceedClicked(false);
        setIsFormValid(isValid ? Date.now() : null);
    };

    const onFormChanged = (data) => {
        const dateFields = config.forms.eventForm.fields.filter(
            (f) => f.type === Input.Date && (f.maxDateField || f.minDateField)
        );
        dateFields && validateDateFromTo(data, dateFields);
        validateNightsCount(data, rawData);
        setSameResponsiblePerson(data, formFeature, rawData);

        setRawData(data);
    };

    const validate =
        ((validateForm.split("-")[0] as unknown) as PageId) === formId ? Number(validateForm.split("-")[1]) : 1;

    return (
        <>
            <Form
                fieldConfig={config.forms.eventForm}
                onChange={onFormChanged}
                validateForm={validate}
                onFormValidated={onFormValidated}
                resetForm={resetForm}
                formValues={Object.keys(rawData).length ? rawData : null}
            ></Form>
            {error ? <div className="network-error">Kļūda servera savienojumā!</div> : null}
        </>
    );
};

const validateNightsCount = (data, rawData) => {
    if (!data["ISFORESTNIGHT"]) {
        delete data["NIGHTSCOUNT"];
    }
    config.forms.eventForm.fields.find((f) => f.field === "NIGHTSCOUNT").hasError = false;

    const calculatedNights =
        data["DATEFROM"] &&
        data["DATETO"] &&
        Math.floor((Date.parse(data["DATETO"]) - Date.parse(data["DATEFROM"])) / 86400000);

    config.forms.eventForm.fields.find((f) => f.field === "ISFORESTNIGHT").visibilityDeterminantField = calculatedNights
        ? undefined
        : "anything";

    !calculatedNights && delete data["ISFORESTNIGHT"];

    const isNightsCountDeleted = rawData["NIGHTSCOUNT"] && !data["NIGHTSCOUNT"];
    const isForestNightToggled = !rawData["ISFORESTNIGHT"] && data["ISFORESTNIGHT"];

    data["ISFORESTNIGHT"] && data["NIGHTSCOUNT"]
        ? (config.forms.eventForm.fields.find((f) => f.field === "NIGHTSCOUNT").hasError =
              data["NIGHTSCOUNT"] > calculatedNights)
        : (data["NIGHTSCOUNT"] = isNightsCountDeleted ? null : isForestNightToggled ? calculatedNights : null);
};

const validateDateFromTo = (data, dateFields) => {
    Object.keys(data).map((item) => {
        if (!dateFields.find((dataField) => dataField.field === item)) {
            return;
        }

        const currentFieldConfig = config.forms.eventForm.fields.find((f) => f.field === item);

        if (currentFieldConfig.maxDateField || currentFieldConfig.minDateField) {
            config.forms.eventForm.fields.map((f) => {
                f.field === currentFieldConfig.maxDateField && (f.minDate = data[item]);
                f.field === currentFieldConfig.minDateField && (f.maxDate = data[item]);
            });
        }

        if (currentFieldConfig.minDateField && data[currentFieldConfig.minDateField]) {
            const isDateValid = data[item] >= data[currentFieldConfig.minDateField];

            config.forms.eventForm.fields.map((f) => {
                f.field === currentFieldConfig.minDateField &&
                    (f.hasError = currentFieldConfig.hasError = !isDateValid);
            });
        }
        if (currentFieldConfig.maxDateField && data[currentFieldConfig.maxDateField]) {
            const isDateValid = data[item] <= data[currentFieldConfig.maxDateField];

            config.forms.eventForm.fields.map((f) => {
                f.field === currentFieldConfig.maxDateField &&
                    (f.hasError = currentFieldConfig.hasError = !isDateValid);
            });
        }
    });
};

const setSameResponsiblePerson = (data, formFeature, rawData) => {
    if (data["SAMEPERSONRESPONSIBLE"]) {
        const isCompany = formFeature.get("APPLICANTTYPE").toString() === "1";

        data["RESPONSIBLEPERSON"] = isCompany ? formFeature.get("CONTACTPERSON") : formFeature.get("APPLICANTNAME");
        data["RESPONSIBLEPERSONPHONE"] = isCompany
            ? formFeature.get("CONTACTPERSONPHONE")
            : formFeature.get("APPLICANTPHONE");
    } else if (
        !data["SAMEPERSONRESPONSIBLE"] &&
        rawData["SAMEPERSONRESPONSIBLE"] &&
        (rawData["RESPONSIBLEPERSON"] || rawData["RESPONSIBLEPERSONPHONE"])
    ) {
        data["RESPONSIBLEPERSON"] = null;
        data["RESPONSIBLEPERSONPHONE"] = null;
    }
};

const convertBooleanToCodedValue = (fieldConfig: IEsriFieldInfo, f: IInputFieldConfig, formData) =>
    fieldConfig && fieldConfig.domain && fieldConfig.domain.codedValues
        ? fieldConfig.domain.codedValues.find((cv) =>
              formData[f.field] ? cv.name.toUpperCase() === "JĀ" : cv.name.toUpperCase() === "NĒ"
          ).code
        : formData[f.field];
